import React from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LabelIcon from "@mui/icons-material/Label";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import HistoryIcon from "@mui/icons-material/History";

const SectionHeader = ({ icon, title }) => (
  <Grid item xs={12}>
    <Box display="flex" alignItems="center" gap={0.75} mt={1.5} mb={0.25}>
      <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        letterSpacing={0.8}
        textTransform="uppercase"
      >
        {title}
      </Typography>
      <Box flex={1} height="1px" bgcolor="divider" ml={0.5} />
    </Box>
  </Grid>
);

const FieldCard = ({ label, value, xs = 3 }) => (
  <Grid item xs={xs}>
    <Box
      sx={{
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 1.5,
        px: 1.25,
        py: 0.75,
        height: "100%",
        transition: "all 0.15s ease",
        "&:hover": { borderColor: "primary.light", bgcolor: "primary.50" },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        textTransform="uppercase"
        letterSpacing={0.4}
        display="block"
        fontSize="0.6rem"
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        fontSize="0.8rem"
        color={value != null && value !== "" ? "text.primary" : "text.disabled"}
      >
        {value != null && value !== "" ? value : "N/A"}
      </Typography>
    </Box>
  </Grid>
);

const ViewCoolerShipping = ({ shipping }) => {
  if (!shipping) return null;

  const formatDate = (val) => (val ? new Date(val).toLocaleString() : null);

  const coolerId = shipping.cooler?.coolerId || shipping.coolerId || null;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={700} letterSpacing={-0.3}>
            Cooler Shipping
          </Typography>
          <Typography variant="caption" color="text.secondary">
            — Shipping Record
          </Typography>
        </Box>
        <Chip
          label="Cooler Shipping"
          color="primary"
          variant="outlined"
          size="small"
          icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
        />
      </Box>

      <Divider sx={{ mb: 0.5 }} />

      <Grid container spacing={1}>
        {/* Cooler Identity */}
        <SectionHeader
          icon={<AcUnitIcon sx={{ fontSize: 14 }} />}
          title="Cooler Information"
        />
        <FieldCard label="Cooler ID" value={coolerId} />
        <FieldCard
          label="Client Account #"
          value={shipping.clientAccountNumber}
        />

        {/* Carrier & Tracking */}
        <SectionHeader
          icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
          title="Carrier & Tracking"
        />
        <FieldCard label="Carrier" value={shipping.carrier} />
        <FieldCard label="Tracking Number" value={shipping.trackingNumber} />

        {/* Label */}
        <SectionHeader
          icon={<LabelIcon sx={{ fontSize: 14 }} />}
          title="Label Details"
        />
        <FieldCard label="Label ID" value={shipping.labelId} />
        <FieldCard label="Label URL" value={shipping.labelUrl} xs={6} />

        {/* Audit Trail */}
        <SectionHeader
          icon={<HistoryIcon sx={{ fontSize: 14 }} />}
          title="Audit Trail"
        />
        <FieldCard
          label="Created Date"
          value={formatDate(shipping.createdDate)}
        />
        <FieldCard label="Created By" value={shipping.createdBy} />
        {shipping.updatedAt && (
          <FieldCard
            label="Updated At"
            value={formatDate(shipping.updatedAt)}
          />
        )}
        {shipping.updatedBy && (
          <FieldCard label="Updated By" value={shipping.updatedBy} />
        )}
      </Grid>
    </Paper>
  );
};

export default ViewCoolerShipping;